import React, { useState } from 'react'
import { useBranding } from '@/contexts/BrandingContext'
import { getPageStyles, getCardStyles, getStatusColors } from '@/utils/pageBranding'
import { DocumentService, DOCUMENT_TYPE_LABELS, validateFile } from '@/services/documentService'
import { helpersService, UploadedFile } from '@/services/helpersService'
import { DocumentUpload as DocumentUploadType } from '@/types'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Upload, FileText, CheckCircle } from 'lucide-react'

interface DocumentUploadProps {
  onUploadSuccess: () => void
  onUploadError: (error: string) => void
}

export const DocumentUpload: React.FC<DocumentUploadProps> = ({
  onUploadSuccess,
  onUploadError,
}) => {
  const { branding } = useBranding()
  const pageStyles = getPageStyles(branding)
  const cardStyles = getCardStyles(branding)
  const statusColors = getStatusColors(branding)
  const [formData, setFormData] = useState<Partial<DocumentUploadType>>({
    document_type: '',
    priority: 'REQUIRED',
  })
  const [file, setFile] = useState<File | null>(null)
  const [uploadedFile, setUploadedFile] = useState<UploadedFile | null>(null)
  const [isUploading, setIsUploading] = useState(false)
  const [dragActive, setDragActive] = useState(false)

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFile = event.target.files?.[0]
    if (selectedFile) {
      const validation = validateFile(selectedFile)
      if (validation.isValid) {
        setFile(selectedFile)
      } else {
        onUploadError(validation.error || 'Invalid file')
      }
    }
  }

  const handleDrag = (e: React.DragEvent) => {
    e.preventDefault()
    e.stopPropagation()
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true)
    } else if (e.type === 'dragleave') {
      setDragActive(false)
    }
  }

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault()
    e.stopPropagation()
    setDragActive(false)

    const droppedFile = e.dataTransfer.files[0]
    if (droppedFile) {
      const validation = validateFile(droppedFile)
      if (validation.isValid) {
        setFile(droppedFile)
      } else {
        onUploadError(validation.error || 'Invalid file')
      }
    }
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()

    if (!file || !formData.document_type) {
      onUploadError('Please select a file and document type')
      return
    }

    setIsUploading(true)

    try {
      // First upload file using helpersService to get URL
      const uploadedFileData = await helpersService.uploadDocument({
        file,
        documentType: 'document',
        purpose: formData.document_type,
      })

      setUploadedFile(uploadedFileData)

      // Then create document record using DocumentService with the uploaded file
      await DocumentService.uploadDocument({
        document_type: formData.document_type,
        file,
        priority: formData.priority || 'REQUIRED',
        expires_at: formData.expires_at,
      })

      onUploadSuccess()
      setFile(null)
      setUploadedFile(null)
      setFormData({
        document_type: '',
        priority: 'REQUIRED',
      })
    } catch (error: any) {
      onUploadError(helpersService.handleError(error))
    } finally {
      setIsUploading(false)
    }
  }

  return (
    <div className="min-h-screen" style={pageStyles.container}>
      <div className="relative z-10 p-6">
        <div className="transition-all duration-300 border card hover:shadow-xl backdrop-blur-sm" style={{ ...cardStyles }}>
          <div className="p-8">
            <div className="mb-8">
              <h3 className="text-2xl font-bold mb-2" style={pageStyles.text}>Upload Document</h3>
              <p style={pageStyles.textSecondary}>Upload your KYC documents for verification</p>
            </div>

            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-semibold mb-3" style={pageStyles.textSecondary}>
                    Document Type *
                  </label>
                  <Select
                    value={formData.document_type}
                    onValueChange={value => setFormData({ ...formData, document_type: value })}
                    required
                  >
                    <SelectTrigger className="hover:opacity-80" style={{
                      backgroundColor: `${branding.surfaceColor}80`,
                      borderColor: `${branding.textSecondaryColor}30`,
                      color: branding.textColor
                    }}>
                      <SelectValue placeholder="Select document type" />
                    </SelectTrigger>
                    <SelectContent className="hover:opacity-80" style={{
                      backgroundColor: `${branding.surfaceColor}90`,
                      borderColor: `${branding.textSecondaryColor}30`
                    }}>
                      {Object.entries(DOCUMENT_TYPE_LABELS).map(([key, label]) => (
                        <SelectItem key={key} value={key}>
                          {label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <label className="block text-sm font-semibold mb-3" style={pageStyles.textSecondary}>Priority</label>
                  <Select
                    value={formData.priority}
                    onValueChange={value => setFormData({ ...formData, priority: value as any })}
                  >
                    <SelectTrigger className="hover:opacity-80" style={{
                      backgroundColor: `${branding.surfaceColor}80`,
                      borderColor: `${branding.textSecondaryColor}30`,
                      color: branding.textColor
                    }}>
                      <SelectValue placeholder="Select priority" />
                    </SelectTrigger>
                    <SelectContent className="hover:opacity-80" style={{
                      backgroundColor: `${branding.surfaceColor}90`,
                      borderColor: `${branding.textSecondaryColor}30`
                    }}>
                      <SelectItem value="REQUIRED">Required</SelectItem>
                      <SelectItem value="OPTIONAL">Optional</SelectItem>
                      <SelectItem value="RECOMMENDED">Recommended</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div>
                <label className="block text-sm font-semibold mb-3" style={pageStyles.textSecondary}>
                  Expiry Date (Optional)
                </label>
                <Input
                  type="date"
                  value={formData.expires_at || ''}
                  onChange={e => setFormData({ ...formData, expires_at: e.target.value })}
                  className="hover:opacity-80"
                  style={{
                    backgroundColor: `${branding.surfaceColor}80`,
                    borderColor: `${branding.textSecondaryColor}30`,
                    color: branding.textColor
                  }}
                />
              </div>

              <div>
                <label className="block text-sm font-semibold mb-3" style={pageStyles.textSecondary}>File *</label>
                <div
                  className={`border-2 border-dashed rounded-2xl p-8 text-center transition-all duration-300 cursor-pointer ${dragActive
                    ? 'scale-105'
                    : 'hover:opacity-80'
                    }`}
                  style={{
                    borderColor: dragActive ? branding.primaryColor : `${branding.textSecondaryColor}30`,
                    backgroundColor: dragActive ? `${branding.primaryColor}10` : `${branding.surfaceColor}20`
                  }}
                  onDragEnter={handleDrag}
                  onDragLeave={handleDrag}
                  onDragOver={handleDrag}
                  onDrop={handleDrop}
                  onClick={() => document.getElementById('file-upload')?.click()}
                >
                  {file ? (
                    <div className="space-y-4">
                      <div className="flex items-center justify-center w-16 h-16 mx-auto rounded-full" style={{ backgroundColor: `${branding.primaryColor}20` }}>
                        <FileText className="w-8 h-8" style={{ color: branding.primaryColor }} />
                      </div>
                      <div>
                        <p className="text-sm font-medium" style={pageStyles.text}>{file.name}</p>
                        <p className="text-xs" style={pageStyles.textSecondary}>
                          {(file.size / (1024 * 1024)).toFixed(2)} MB
                        </p>
                      </div>

                      {uploadedFile && (
                        <div className="mt-4 p-4 border rounded-lg" style={{ backgroundColor: statusColors.success.backgroundColor, borderColor: statusColors.success.borderColor }}>
                          <div className="flex items-center space-x-2 mb-2">
                            <CheckCircle className="w-5 h-5" style={{ color: statusColors.success.color }} />
                            <span className="text-sm font-medium" style={{ color: statusColors.success.color }}>
                              File uploaded successfully!
                            </span>
                          </div>
                          <p className="text-xs mb-2" style={{ color: statusColors.success.color }}>
                            Preview URL: {uploadedFile.url}
                          </p>
                          <Button
                            type="button"
                            variant="outline"
                            size="sm"
                            onClick={() => window.open(uploadedFile.url, '_blank')}
                            className="text-xs hover:opacity-80"
                            style={{ backgroundColor: statusColors.info.backgroundColor, borderColor: statusColors.info.borderColor, color: branding.textColor }}
                          >
                            Preview Document
                          </Button>
                        </div>
                      )}

                      <div className="flex space-x-2">
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          onClick={() => {
                            setFile(null)
                            setUploadedFile(null)
                          }}
                          className="hover:opacity-80"
                          style={{ backgroundColor: statusColors.error.backgroundColor, borderColor: statusColors.error.borderColor, color: branding.textColor }}
                        >
                          Remove
                        </Button>
                      </div>
                    </div>
                  ) : (
                    <div className="space-y-4">
                      <div className="flex items-center justify-center w-16 h-16 mx-auto rounded-full" style={{ backgroundColor: `${branding.surfaceColor}30` }}>
                        <Upload className="w-8 h-8" style={{ color: branding.textSecondaryColor }} />
                      </div>
                      <div>
                        <p className="text-sm font-medium" style={pageStyles.text}>
                          Click here or drag and drop your file
                        </p>
                        <p className="text-xs mt-1" style={pageStyles.textSecondary}>
                          Supported formats: PDF, JPG, PNG, DOC, DOCX (Max 10MB)
                        </p>
                      </div>
                      <Input
                        type="file"
                        accept=".pdf,.jpg,.jpeg,.png,.doc,.docx"
                        onChange={handleFileChange}
                        className="hidden"
                        id="file-upload"
                        key={file ? 'file-selected' : 'no-file'}
                      />
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={() => document.getElementById('file-upload')?.click()}
                        className="hover:opacity-80"
                        style={{ backgroundColor: `${branding.primaryColor}20`, borderColor: `${branding.primaryColor}40`, color: branding.primaryColor }}
                      >
                        Choose File
                      </Button>
                    </div>
                  )}
                </div>
              </div>

              <div className="flex justify-between pt-4">
                <div className="flex space-x-3">
                  {file && !uploadedFile && (
                    <Button
                      type="button"
                      onClick={async () => {
                        if (!file || !formData.document_type) {
                          onUploadError('Please select a file and document type')
                          return
                        }

                        setIsUploading(true)
                        try {
                          const uploadedFileData = await helpersService.uploadDocument({
                            file,
                            documentType: 'document',
                            purpose: formData.document_type,
                          })
                          setUploadedFile(uploadedFileData)
                        } catch (error: any) {
                          onUploadError(helpersService.handleError(error))
                        } finally {
                          setIsUploading(false)
                        }
                      }}
                      disabled={isUploading}
                      className="hover:opacity-80"
                      style={{ backgroundColor: statusColors.info.backgroundColor, color: branding.textColor }}
                    >
                      {isUploading ? (
                        <div className="flex items-center space-x-2">
                          <div className="animate-spin rounded-full h-4 w-4 border-b-2" style={{ borderColor: `${branding.primaryColor} transparent transparent transparent` }}></div>
                          <span>Uploading...</span>
                        </div>
                      ) : (
                        'Upload File'
                      )}
                    </Button>
                  )}

                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => {
                      setFile(null)
                      setUploadedFile(null)
                      setFormData({
                        document_type: '',
                        priority: 'REQUIRED',
                      })
                    }}
                    className="hover:opacity-80"
                    style={{ backgroundColor: `${branding.surfaceColor}40`, borderColor: `${branding.textSecondaryColor}30`, color: branding.textSecondaryColor }}
                  >
                    Cancel
                  </Button>
                </div>

                <Button
                  type="submit"
                  disabled={!uploadedFile || !formData.document_type || isUploading}
                  className="hover:opacity-80"
                  style={pageStyles.primaryBg}
                >
                  {isUploading ? (
                    <div className="flex items-center space-x-2">
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                      <span>Submitting...</span>
                    </div>
                  ) : (
                    'Submit Document'
                  )}
                </Button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>
  )
}
